import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Table, Tag, Spin, Alert, Button, Space, Modal, Form, Input, InputNumber, Select, message } from 'antd';
import {
  CarOutlined, TeamOutlined, SafetyOutlined, BellOutlined,
  FileTextOutlined, IdcardOutlined, ExperimentOutlined, ReloadOutlined,
  ExclamationCircleOutlined,
  FolderOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
// 2026-10-29：待补资料提示条（历史数据补录期间的过渡组件，数据补齐后自动隐藏）
import DataQualityBar from '../components/DataQualityBar';
import dayjs from 'dayjs';
import { hasPermission, PERMS } from '../utils/permissions';
import SafeDatePicker from '../components/SafeDatePicker';
import RoleDashboard from '../components/RoleDashboard';

const { Option } = Select;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  // ⭐ 2026-10-30 三套角色视图（月度数据）
  const [monthly, setMonthly] = useState(null);
  // 当前登录用户（用于判定角色视图）
  const currentUser = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const perms = JSON.parse(localStorage.getItem('permissions') || '[]');
      return { ...u, role: u.role || localStorage.getItem('role') || '', permissions: perms };
    } catch (e) {
      return { role: localStorage.getItem('role') || '', permissions: [] };
    }
  })();
  const [incompleteData, setIncompleteData] = useState(null);
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  const [incompleteLoading, setIncompleteLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [city, setCity] = useState('');
  const [company, setCompany] = useState('');
  const [expiringItems, setExpiringItems] = useState([]);
  const [expiringSummary, setExpiringSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [renewRecord, setRenewRecord] = useState(null);
  const [renewLoading, setRenewLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // 加载公司列表
  useEffect(() => {
    api.get('/companies/list').then(res => {
      setCompanies(res.data || []);
    }).catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (city) params.city = city;
      if (company) params.company = company;
      // 首页只拉轻量计数 + 待办前 8 条，明细在弹窗/独立页才加载
      const [statsRes, itemsRes, incompleteRes, monthlyRes] = await Promise.all([
        api.get('/dashboard/stats', { params }),
        api.get('/dashboard/expiring-items', { params: { ...params, limit: 8 } }),
        api.get('/dashboard/incomplete-count', { params }),
        api.get('/dashboard/monthly', { params }).catch(() => ({ data: null }))
      ]);
      setMonthly(monthlyRes?.data || null);
      setIncompleteData(incompleteRes.data);
      setStats(statsRes.data);
      const payload = itemsRes.data || {};
      if (Array.isArray(payload)) {
        setExpiringItems(() => [...payload].sort((a, b) => (a.days_left ?? 9999) - (b.days_left ?? 9999)));
        setExpiringSummary(null);
      } else {
        // 新格式：{ items, total, by_type }
        setExpiringItems(payload.items || []);
        setExpiringSummary({ total: payload.total || 0, by_type: payload.by_type || {} });
      }
    } catch (err) {
      console.error('加载数据失败', err);
    }
    setLoading(false);
  }, [city, company]);

  useEffect(() => { loadData(); }, [loadData]);

  const openRenew = (record) => {
    setRenewRecord(record);
    form.setFieldsValue({
      insurance_company: record.insurance_company,
      premium: undefined,
      policy_number: '',
      start_date: dayjs(record.end_date).add(1, 'day'),
      end_date: dayjs(record.end_date).add(1, 'year').subtract(1, 'day'),
    });
    setRenewModalOpen(true);
  };

  const handleRenew = async () => {
    const values = await form.validateFields();
    if (!renewRecord) return;
    setRenewLoading(true);
    try {
      await api.post('/insurance/renew', {
        old_record_id: renewRecord.id,
        policy_year: dayjs().year(),
        insurance_company: values.insurance_company,
        policy_number: values.policy_number,
        premium: values.premium,
        start_date: values.start_date?.format('YYYY-MM-DD'),
        end_date: values.end_date?.format('YYYY-MM-DD'),
        notes: `续保自保单 #${renewRecord.id}`,
      });
      message.success('续保成功，原保单已归档');
      setRenewModalOpen(false);
      loadData();
    } catch (err) {
      message.error(err.response?.data?.detail || '续保失败');
    }
    setRenewLoading(false);
  };

  // 不再整页 Spin，改为渐进式渲染：统计卡片/待办各自显示加载态

  const statCards = [
    { title: '车辆总数', value: stats?.vehicle_count || 0, icon: <CarOutlined />, color: '#1890ff', path: '/vehicles' },
    { title: '司机总数', value: stats?.driver_count || 0, icon: <TeamOutlined />, color: '#52c41a', path: '/vehicles' },
    { title: '保险到期提醒', value: stats?.insurance_expiring || 0, icon: <SafetyOutlined />, color: '#faad14', path: '/insurance' },
    { title: '运输证审验', value: stats?.permit_expiring || 0, icon: <FileTextOutlined />, color: '#ff4d4f', path: '/inspections' },
    { title: '车辆年检', value: stats?.inspection_expiring || 0, icon: <BellOutlined />, color: '#722ed1', path: '/inspections' },
    { title: '驾驶证到期', value: stats?.license_expiring || 0, icon: <IdcardOutlined />, color: '#ff7a45', path: '/inspections' },
    { title: '资格证到期', value: stats?.cert_expiring || 0, icon: <BellOutlined />, color: '#eb2f96', path: '/inspections' },
    { title: '再教育到期', value: stats?.edu_expiring || 0, icon: <ExperimentOutlined />, color: '#fa8c16', path: '/inspections' },
    { title: '资料不完整', value: incompleteData?.total || 0, icon: <ExclamationCircleOutlined />, color: '#ff4d4f', onClick: () => openIncompleteDetail() },
  ];

  // 卡片跳转时携带当前城市/公司筛选，让列表页跟着筛选
  const goWithFilter = (path) => {
    const qs = new URLSearchParams();
    if (city) qs.set('city', city);
    if (company) qs.set('company', company);
    const q = qs.toString();
    navigate(q ? `${path}?${q}` : path);
  };

  // 点击卡片时才拉取完整明细（数据量大，首页不预加载）
  const openIncompleteDetail = async () => {
    const params = {};
    if (city) params.city = city;
    if (company) params.company = company;
    setIncompleteLoading(true);
    setIncompleteOpen(true);
    try {
      const res = await api.get('/dashboard/incomplete', { params });
      setIncompleteData(res.data);
    } catch (e) {
      message.error('加载资料不完整列表失败');
    }
    setIncompleteLoading(false);
  };

  const insColumns = [
    { title: '车牌号', dataIndex: 'plate_number', key: 'plate_number' },
    { title: '险种', dataIndex: 'insurance_type', key: 'insurance_type', width: 100,
      render: val => {
        const colors = { '交强险': 'blue', '商业险': 'green', '承运人责任险': 'orange', '承运险': 'orange' };
        return <Tag color={colors[val] || 'default'}>{val}</Tag>;
      }
    },
    { title: '保险公司', dataIndex: 'insurance_company', key: 'insurance_company' },
    { title: '到期日期', dataIndex: 'end_date', key: 'end_date', width: 110 },
    { title: '剩余天数', dataIndex: 'days_left', key: 'days_left', width: 90,
      render: days => (
        <Tag color={days <= 7 ? 'red' : days <= 15 ? 'orange' : 'blue'}>{days}天</Tag>
      )
    },
    {
      title: '操作', key: 'action', width: 120,
      render: (_, record) => (
        hasPermission(PERMS.INSURANCE_CREATE) || hasPermission(PERMS.INSURANCE_EDIT) ? (
          <Button type="primary" size="small" onClick={() => navigate(`/vehicles/${record.vehicle_id}?tab=insurance`)}>
            去续保
          </Button>
        ) : <Tag color="default">仅查看</Tag>
      )
    },
  ];

  // 去重获取城市列表
  const cityList = [...new Set(companies.map(c => c.city).filter(Boolean))];

  return (
    <div>
      {/* 2026-10-29：待补资料提示条 —— 数据没补全期间的单独入口。
          后端 strict_data_mode=1 后自动返回 hidden=true，此处不渲染，无需改前端。 */}
      <DataQualityBar />
      {/* 筛选栏 */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={16} align="middle">
          <Col>
            <span style={{ marginRight: 8, fontSize: 13, color: '#666' }}>城市：</span>
            <Select
              allowClear
              placeholder="全部城市"
              value={city || undefined}
              onChange={val => { setCity(val || ''); setCompany(''); }}
              style={{ width: 120 }}
            >
              {cityList.map(c => <Option key={c} value={c}>{c}</Option>)}
            </Select>
          </Col>
          <Col>
            <span style={{ marginRight: 8, fontSize: 13, color: '#666' }}>公司：</span>
            <Select
              allowClear
              placeholder="全部公司"
              value={company || undefined}
              onChange={val => setCompany(val || '')}
              style={{ width: 160 }}
            >
              {companies
                .filter(c => !city || c.city === city)
                .map(c => <Option key={c.id} value={c.name}>{c.name}</Option>)
              }
            </Select>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} size="small" onClick={loadData}>刷新</Button>
          </Col>
        </Row>
      </Card>

      {/* ⭐ 2026-10-30 三套角色视图：城市管理员 / 财务 / 老板 */}
      <RoleDashboard
        user={currentUser}
        data={monthly}
        city={city}
        onJump={(path) => navigate(path)}
      />

      <div style={{ height: 8 }} />

      {/* ⭐ 2026-10-30：旧 9 张卡归到「数据预警」分区，与本月经营分开 */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        margin: '18px 0 10px',
      }}>
        <span style={{ width: 3, height: 14, borderRadius: 2, background: 'linear-gradient(180deg,#fa8c16,#f5222d)' }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#1f2733' }}>数据预警</span>
        <span style={{ fontSize: 12, color: '#b0b7c3', marginLeft: 4 }}>点击进入对应列表</span>
      </div>

      <Row gutter={[16, 16]}>
        {statCards.map((card, index) => (
          <Col xs={12} sm={12} md={8} lg={6} xl={6} key={index}>
            <Card
              hoverable
              className="stat-card"
              onClick={() => card.onClick ? card.onClick() : goWithFilter(card.path)}
              styles={{ body: { padding: '16px 18px', height: '100%', display: 'flex', alignItems: 'center' } }}
              style={{ color: card.color, borderRadius: 12, height: '100%', border: '1px solid #eef1f6' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="stat-icon" style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}cc)` }}>
                  {card.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: '#8c96a5', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={card.title}>{card.title}</div>
                  <div className="stat-value" style={{ color: card.color }}>{card.value}</div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal
        open={incompleteOpen}
        onCancel={() => setIncompleteOpen(false)}
        footer={null}
        width={940}
        styles={{ body: { paddingTop: 8 } }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#fff1f0', color: '#ff4d4f', fontSize: 20, flexShrink: 0,
            }}>
              <ExclamationCircleOutlined />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', lineHeight: 1.3 }}>资料不完整档案</div>
              <div style={{ fontSize: 12, fontWeight: 400, color: '#98a2b3' }}>
                共 <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{incompleteData?.total || 0}</span> 条记录待补齐
              </div>
            </div>
          </div>
        }
      >
        {incompleteData?.by_city && Object.keys(incompleteData.by_city).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {Object.entries(incompleteData.by_city).map(([c, n]) => (
              <div key={c} style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 20,
                background: 'linear-gradient(135deg, #fff7e6, #fff1f0)', border: '1px solid #ffe0b2',
              }}>
                <span style={{ fontSize: 13, color: '#616e7c' }}>{c}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#fa8c16' }}>{n}</span>
              </div>
            ))}
          </div>
        )}
        <Table
          size="small"
          rowKey="vehicle_id"
          loading={incompleteLoading}
          dataSource={incompleteData?.items || []}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 760 }}
          columns={[
            { title: '城市', dataIndex: 'city', key: 'city', width: 72,
              render: (t) => <span style={{ color: '#616e7c' }}>{t || '—'}</span> },
            { title: '车牌号', dataIndex: 'plate_number', key: 'plate_number', width: 118,
              render: (t, r) => (
                <a onClick={() => { setIncompleteOpen(false); navigate(`/vehicles/${r.vehicle_id}`); }}
                  style={{ fontWeight: 600 }}>{t || '—'}</a>
              ) },
            { title: '司机', dataIndex: 'driver_name', key: 'driver_name', width: 96,
              render: (t) => t || <span style={{ color: '#c0c7d1' }}>未绑定</span> },
            { title: '公司', dataIndex: 'company', key: 'company', width: 150,
              render: (t) => <span style={{ color: '#616e7c' }}>{t || '—'}</span> },
            { title: '缺失项', dataIndex: 'missing', key: 'missing',
              render: (arr) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(arr || []).map((m, i) => (
                    <span key={i} style={{
                      display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 12,
                      fontSize: 12, lineHeight: '18px', background: '#fff1f0', color: '#cf1322',
                      border: '1px solid #ffccc7', whiteSpace: 'nowrap',
                    }}>{m}</span>
                  ))}
                </div>
              ) },
          ]}
        />
      </Modal>

      {/* 待办提醒（含保险到期）— 首页精简摘要版，完整操作在「待办提醒」菜单页 */}
      <Card title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <BellOutlined style={{ color: '#ff4d4f' }} />
          待办事项
          <span style={{ fontSize: 12, fontWeight: 400, color: '#999' }}>
            共 {expiringSummary?.total ?? expiringItems.length} 项
          </span>
        </span>
      } style={{ marginTop: 16, borderRadius: 12 }}
        extra={<Space>
        <Button size="small" type="primary" ghost onClick={() => goWithFilter('/todos')}>查看全部 →</Button>
      </Space>}>
        {loading ? (
          <Spin style={{ display: 'block', margin: '24px auto' }} />
        ) : expiringItems.length === 0 ? (
          <Alert message="30天内无即将到期的项目" type="success" showIcon />
        ) : (
          <>
            {/* 分类摘要（用全量 by_type 统计，点击跳菜单页） */}
            {(() => {
              const byType = expiringSummary?.by_type || {};
              const typeColorMap = { '保险': '#fa8c16', '保险到期': '#fa8c16', '审验': '#1890ff', '车辆审验': '#1890ff', '审验到期': '#1890ff', '年检': '#722ed1', '车辆年检': '#722ed1' };
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  {Object.entries(byType).map(([k, n]) => (
                    <Tag key={k} color={typeColorMap[k] || 'red'} style={{ padding: '3px 12px', borderRadius: 20, fontSize: 13, margin: 0, cursor: 'pointer' }}
                      onClick={() => goWithFilter('/todos')}>
                      {k} {n}
                    </Tag>
                  ))}
                </div>
              );
            })()}
            {/* 最紧急 N 条：紧凑行，整行可点，直接跳对应页 */}
            <div>
              {expiringItems.map((item, idx) => {
                const daysLeft = item.days_left;
                const isMonthType = item.type === 'inspection_due' || item.type === 'inspection';
                const now = new Date();
                const curYM = now.getFullYear() * 12 + (now.getMonth() + 1);
                let monthText = '';
                let monthColor = 'blue';
                if (isMonthType) {
                  const em = (item.end_date || '').match(/^(\d{4})-(\d{2})/);
                  if (em) {
                    const endYM = parseInt(em[1], 10) * 12 + parseInt(em[2], 10);
                    const diff = endYM - curYM;
                    if (diff < 0) { const n = -diff; monthText = n === 1 ? '已过期1个月' : `已过期${n}个月`; monthColor = 'red'; }
                    else if (diff === 0) { monthText = '本月到期'; monthColor = 'red'; }
                    else if (diff === 1) { monthText = '下月到期'; monthColor = 'orange'; }
                    else { monthText = `${diff}个月后到期`; monthColor = 'blue'; }
                  }
                }
                const isUrgent = isMonthType ? (monthColor === 'red') : daysLeft <= 7;
                const isWarning = isMonthType ? (monthColor === 'orange') : daysLeft <= 15;
                const typeIconMap = {
                  insurance: <SafetyOutlined style={{ color: '#fa8c16' }} />,
                  license: <IdcardOutlined style={{ color: '#ff4d4f' }} />,
                  qualification: <IdcardOutlined style={{ color: '#722ed1' }} />,
                  education: <ExperimentOutlined style={{ color: '#faad14' }} />,
                  permit: <FileTextOutlined style={{ color: '#ff4d4f' }} />,
                  inspection: <CarOutlined style={{ color: '#1890ff' }} />,
                  inspection_due: <BellOutlined style={{ color: '#1890ff' }} />,
                  archive_expiry: <FolderOutlined style={{ color: '#8c8c8c' }} />,
                };
                const icon = typeIconMap[item.type] || typeIconMap.insurance;
                const barColor = isUrgent ? '#ff4d4f' : isWarning ? '#fa8c16' : '#52c41a';
                const tagColor = isMonthType ? monthColor : (isUrgent ? 'red' : isWarning ? 'orange' : 'blue');
                const tagText = isMonthType ? monthText : (daysLeft <= 0 ? '已过期' : `${daysLeft}天后到期`);
                const go = () => {
                  if (item.type === 'insurance') {
                    const vid = item.vehicle_id || item.related_id;
                    vid ? navigate(`/vehicles/${vid}?tab=insurance`) : navigate('/insurance');
                  } else if (item.type === 'archive_expiry') {
                    navigate(`/vehicles/${item.related_id}`);
                  } else if (item.type === 'inspection_due' || item.type === 'inspection') {
                    navigate(`/vehicles/${item.related_id}?tab=inspection`);
                  } else {
                    navigate('/vehicles');
                  }
                };
                return (
                  <div
                    key={idx}
                    onClick={go}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '7px 12px', marginBottom: 4, borderRadius: 8,
                      border: '1px solid #eef1f6', borderLeft: `3px solid ${barColor}`,
                      background: isUrgent ? '#fff2f0' : '#fff', cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = isUrgent ? '#ffe7e5' : '#f7faff')}
                    onMouseLeave={e => (e.currentTarget.style.background = isUrgent ? '#fff2f0' : '#fff')}
                  >
                    <span style={{ fontSize: 15, lineHeight: 1 }}>{icon}</span>
                    <span style={{ fontWeight: 500, fontSize: 13, minWidth: 76 }}>{item.type_label}</span>
                    <span style={{ fontSize: 12, color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}{item.plate_number ? `（${item.plate_number}）` : ''}
                    </span>
                    <span style={{ fontSize: 12, color: '#999' }}>{item.end_date}</span>
                    <Tag color={tagColor} style={{ margin: 0, fontSize: 11 }}>{tagText}</Tag>
                  </div>
                );
              })}
            </div>
            {expiringSummary?.total > expiringItems.length && (
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Button type="link" size="small" onClick={() => goWithFilter('/todos')}>
                  还有 {expiringSummary.total - expiringItems.length} 项，查看全部 →
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* 续保单窗 */}
      <Modal
        title={`续保 - ${renewRecord?.plate_number || ''} ${renewRecord?.insurance_type || ''}`}
        open={renewModalOpen}
        onOk={handleRenew}
        onCancel={() => setRenewModalOpen(false)}
        confirmLoading={renewLoading}
        okText="确认续保"
        cancelText="取消"
        width={500}
        destroyOnClose
      >
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, fontSize: 13, color: '#d46b08' }}>
          续保后原保单将自动归档保存
        </div>
        <Form form={form} layout="vertical">
          <Form.Item name="insurance_company" label="保险公司"
            rules={[{ required: true, message: '请选择/输入保险公司' }]}>
            <Input placeholder="保险公司名称" />
          </Form.Item>
          <Form.Item name="policy_number" label="保单号">
            <Input placeholder="新保单号" />
          </Form.Item>
          <Form.Item name="premium" label="保费(元)" rules={[{ required: true, message: '请输入保费' }]}>
            <InputNumber min={0} style={{ width: '100%' }} placeholder="保费金额" />
          </Form.Item>
          <Form.Item name="start_date" label="起保日期" rules={[{ required: true }]}>
            <SafeDatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end_date" label="到期日期" rules={[{ required: true }]}>
            <SafeDatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
